/* ============================================================
   AddressBookSection — CB-3 (31de9d9f): the «Адреса доставки»
   section of the profile drawer. Lists the user's saved addresses
   (default first), supports add / edit / delete / set-default;
   the address field offers DaData suggestions via AddressInput.
   ============================================================ */

import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import type { AddressBookEntry } from '../types/address';
import { AddressInput } from './AddressInput';
import {
  MAX_ENTRIES,
  addressLineError,
  buildEntryRequest,
  canSubmitEntry,
  entrySubtitle,
  entryTitle,
  labelError,
} from './addressBookView';

type Draft = { id: string | null; label: string; address: string; makeDefault: boolean };

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 14px',
  fontSize: 'var(--fs-body)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--color-separator)',
  background: 'var(--color-surface)',
};

const muted: React.CSSProperties = {
  fontSize: 'var(--fs-caption)',
  color: 'var(--color-text-secondary)',
};

const emptyDraft: Draft = { id: null, label: '', address: '', makeDefault: false };

export function AddressBookSection() {
  const [entries, setEntries] = useState<AddressBookEntry[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [cue, setCue] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setEntries(await api.getAddressBook());
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Не удалось загрузить адреса');
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const startAdd = () => {
    setDraft({ ...emptyDraft });
    setFormError(null);
    setCue(null);
  };

  const startEdit = (entry: AddressBookEntry) => {
    setDraft({ id: entry.id, label: entry.label, address: entry.addressLine, makeDefault: entry.isDefault });
    setFormError(null);
    setCue(null);
  };

  const save = async () => {
    if (!draft) return;
    if (labelError(draft.label)) {
      setFormError(labelError(draft.label)!);
      return;
    }
    if (addressLineError(draft.address)) {
      setFormError(addressLineError(draft.address) ?? 'Введите адрес');
      return;
    }
    if (!canSubmitEntry(draft.label, draft.address)) {
      setFormError('Проверьте адрес — он слишком короткий');
      return;
    }

    setBusy(true);
    setFormError(null);
    setCue(null);
    try {
      const request = buildEntryRequest(draft.label, draft.address, draft.makeDefault);
      if (draft.id) {
        await api.updateAddressBookEntry(draft.id, request);
        setCue('Адрес обновлён');
      } else {
        await api.createAddressBookEntry(request);
        setCue('Адрес сохранён');
      }
      setDraft(null);
      await reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Не удалось сохранить адрес');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (entry: AddressBookEntry) => {
    setBusy(true);
    setFormError(null);
    try {
      await api.deleteAddressBookEntry(entry.id);
      if (draft?.id === entry.id) setDraft(null);
      await reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Не удалось удалить адрес');
    } finally {
      setBusy(false);
    }
  };

  const makeDefault = async (entry: AddressBookEntry) => {
    setBusy(true);
    setFormError(null);
    try {
      await api.setDefaultAddressBookEntry(entry.id);
      await reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Не удалось выбрать адрес по умолчанию');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-md)',
        borderTop: '1px solid var(--color-separator)',
        paddingTop: 'var(--space-lg)',
      }}
    >
      <div>
        <p style={{ fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-bold)' }}>Адреса доставки</p>
        <p style={{ ...muted, marginTop: 2 }}>
          Сохранённые адреса появятся в корзине — «по умолчанию» выбран заранее.
        </p>
      </div>

      {loadError && <p style={{ ...muted, color: 'var(--color-danger)' }}>{loadError}</p>}
      {entries === null && !loadError && <p style={muted}>Загрузка адресов…</p>}

      {entries !== null && entries.length === 0 && !draft && (
        <p style={muted}>Пока ни одного адреса — добавьте первый.</p>
      )}

      {entries?.map((entry) => (
        <div
          key={entry.id}
          className="card"
          style={{ padding: 'var(--space-sm) var(--space-md)', display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 'var(--fw-semibold)', fontSize: 'var(--fs-body)' }}>{entryTitle(entry)}</span>
            {entry.isDefault && (
              <span
                style={{
                  fontSize: 'var(--fs-caption)',
                  color: 'var(--color-primary)',
                  fontWeight: 'var(--fw-semibold)',
                }}
              >
                · по умолчанию
              </span>
            )}
          </div>
          <p style={{ ...muted, overflowWrap: 'anywhere' }}>{entrySubtitle(entry)}</p>
          <div style={{ display: 'flex', gap: 'var(--space-md)', flexWrap: 'wrap' }}>
            {!entry.isDefault && (
              <button type="button" disabled={busy} onClick={() => void makeDefault(entry)} style={{ ...muted, color: 'var(--color-primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                Сделать по умолчанию
              </button>
            )}
            <button type="button" disabled={busy} onClick={() => startEdit(entry)} style={{ ...muted, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              Изменить
            </button>
            <button type="button" disabled={busy} onClick={() => void remove(entry)} style={{ ...muted, color: 'var(--color-danger)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              Удалить
            </button>
          </div>
        </div>
      ))}

      {draft && (
        <form
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)' }}
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ ...muted, fontWeight: 'var(--fw-semibold)' }}>Название (необязательно)</span>
            <input
              type="text"
              value={draft.label}
              onChange={(e) => setDraft({ ...draft, label: e.target.value })}
              placeholder="Дом, работа…"
              maxLength={50}
              style={inputStyle}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ ...muted, fontWeight: 'var(--fw-semibold)' }}>Адрес</span>
            <AddressInput
              value={draft.address}
              onChange={(address) => setDraft({ ...draft, address })}
              placeholder="Город, улица, дом, квартира"
            />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)', ...muted }}>
            <input
              type="checkbox"
              checked={draft.makeDefault}
              onChange={(e) => setDraft({ ...draft, makeDefault: e.target.checked })}
            />
            Использовать по умолчанию
          </label>
          {formError && <p style={{ ...muted, color: 'var(--color-danger)' }}>{formError}</p>}
          <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? 'Сохранение…' : draft.id ? 'Сохранить' : 'Добавить адрес'}
            </button>
            <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => setDraft(null)}>
              Отмена
            </button>
          </div>
        </form>
      )}

      {cue && <p style={{ ...muted, color: 'var(--color-success)' }}>{cue}</p>}

      {!draft && (entries?.length ?? 0) < MAX_ENTRIES && (
        <button type="button" className="btn btn-secondary" onClick={startAdd} disabled={busy}>
          + Добавить адрес
        </button>
      )}
    </div>
  );
}
